import React, { useState, useEffect, useCallback } from 'react';
import clientPortalService from '../../services/clientPortalService';
import { getDiagnostics, registerErrorCapture } from '../../utils/portalDiagnostics';

// Persistent "Report a problem" affordance on every portal screen (mounted once
// in WebApp). Framed as a problem report — no reply-expectation, no case
// linkage; case questions go to Messages. Diagnostics are auto-attached on
// submit (the point of the feature). The client sees their own report status so
// a report never silently disappears.

const STATUS_LABEL = { open: 'Received', in_progress: 'Being looked at', resolved: 'Resolved' };
const STATUS_COLOR = {
  open: '#f59e0b', in_progress: '#38bdf8', resolved: '#34d399',
};

export default function PortalReportProblem() {
  const [open, setOpen] = useState(false);
  const [body, setBody] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);
  const [tickets, setTickets] = useState([]);

  useEffect(() => { registerErrorCapture(); }, []);

  const loadTickets = useCallback(async () => {
    try { setTickets(await clientPortalService.getMyTickets()); } catch (e) {}
  }, []);

  useEffect(() => { if (open) loadTickets(); }, [open, loadTickets]);

  const submit = async () => {
    if (!body.trim() || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await clientPortalService.createTicket(body.trim(), getDiagnostics());
      setDone(true);
      setBody('');
      loadTickets();
    } catch (e) {
      setError('Could not send your report. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const close = () => { setOpen(false); setDone(false); setError(null); };

  const btn = {
    position: 'fixed', left: 'max(16px, env(safe-area-inset-left))',
    bottom: 'max(16px, env(safe-area-inset-bottom))', zIndex: 2147483000,
    background: '#0f2740', color: '#e6f0ff', border: '1px solid #2b6cb0',
    borderRadius: 999, padding: '10px 14px', font: '600 13px system-ui',
    boxShadow: '0 4px 14px rgba(0,0,0,.35)', cursor: 'pointer',
    WebkitTapHighlightColor: 'transparent',
  };

  return (
    <>
      {!open && (
        <button style={btn} onClick={() => setOpen(true)} aria-label="Report a problem">
          🛠️ Report a problem
        </button>
      )}
      {open && (
        <div
          onClick={close}
          style={{
            position: 'fixed', inset: 0, zIndex: 2147483001,
            background: 'rgba(3,10,20,.6)', display: 'flex',
            alignItems: 'flex-end', justifyContent: 'center',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#0b1c2e', color: '#e6f0ff', width: '100%',
              maxWidth: 480, borderRadius: '16px 16px 0 0',
              border: '1px solid #1f4468', borderBottom: 'none',
              padding: '18px 18px calc(18px + env(safe-area-inset-bottom))',
              maxHeight: '85vh', overflowY: 'auto', font: '14px system-ui',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Report a problem</h2>
              <button onClick={close} style={{ background: 'none', border: 'none', color: '#9fb6d0', fontSize: 22, cursor: 'pointer' }}>×</button>
            </div>

            {!done ? (
              <>
                <p style={{ color: '#9fb6d0', fontSize: 13, marginTop: 6, marginBottom: 12 }}>
                  Something not working right in the portal? Tell us what happened —
                  we'll attach technical details automatically so we can find it.
                  <br />
                  <strong style={{ color: '#c9d9ec' }}>A question about your case?</strong>{' '}
                  Please use <strong>Messages</strong> instead — this form is for portal problems.
                </p>
                <textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  placeholder="e.g. I tapped download on a document and nothing happened."
                  rows={4}
                  style={{
                    width: '100%', boxSizing: 'border-box', background: '#07131f',
                    color: '#e6f0ff', border: '1px solid #234863', borderRadius: 10,
                    padding: 10, font: '14px system-ui', resize: 'vertical',
                  }}
                />
                {error && <p style={{ color: '#fca5a5', fontSize: 13 }}>{error}</p>}
                <button
                  onClick={submit}
                  disabled={!body.trim() || submitting}
                  style={{
                    marginTop: 10, width: '100%',
                    backgroundColor: body.trim() ? '#2b6cb0' : '#22384c',
                    color: '#fff', border: 'none', borderRadius: 10, padding: '12px',
                    font: '600 15px system-ui', cursor: body.trim() ? 'pointer' : 'default',
                  }}
                >
                  {submitting ? 'Sending…' : 'Send report'}
                </button>
              </>
            ) : (
              <div style={{ marginTop: 10 }}>
                <p style={{ fontSize: 15 }}>✅ Thanks — we've got your report and the details we need. You can check its status below.</p>
                <button onClick={() => setDone(false)} style={{ marginTop: 6, background: 'none', border: '1px solid #234863', color: '#c9d9ec', borderRadius: 8, padding: '8px 12px', cursor: 'pointer' }}>
                  Report another problem
                </button>
              </div>
            )}

            {tickets.length > 0 && (
              <div style={{ marginTop: 18 }}>
                <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '.05em', color: '#7f97b0', marginBottom: 6 }}>Your reports</div>
                {tickets.map((t) => (
                  <div key={t.id} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '8px 0', borderTop: '1px solid #12293e' }}>
                    <span style={{ marginTop: 3, width: 8, height: 8, borderRadius: 8, flex: '0 0 auto', background: STATUS_COLOR[t.status] || '#64748b' }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, color: '#dbe7f3', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {t.body || '(no description)'}
                      </div>
                      <div style={{ fontSize: 11, color: '#7f97b0' }}>
                        {STATUS_LABEL[t.status] || t.status}
                        {t.created_at ? ` · ${new Date(t.created_at).toLocaleDateString()}` : ''}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
